
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { ChildPatientAssessmentFormData } from "../models/types";


const schema = yup.object().shape({
  creationInfo: yup.object({
    datetime: yup.date().required(),
    createdBy: yup.string(),
  }),
  identifyingPerson: yup.object({
    relationship: yup.string(),
    name: yup.string().required(),
    surname: yup.string().required(),
  }),
  safetyMeasures: yup.object({
    serviceIntroduced: yup.boolean(),
    bedRailsUp: yup.boolean(),
    roomIntroduced: yup.boolean(),
    bedsideArrangement: yup.boolean(),
  }),
  parentInfo: yup.object({
    motherFullName: yup.string(),
    motherIdentityNumber: yup.string().max(11),
    fatherFullName: yup.string(),
    fatherIdentityNumber: yup.string().max(11),
  }),
  neuromuscular: yup.object({}),
  hospitalization: yup.object({}),
  catheters: yup.object({}),
  dailyLiving: yup.object({}),
  sensoryAndComm: yup.object({}),
  systemsAssessment: yup.object({}),
});

const defaultValues: ChildPatientAssessmentFormData = {
  creationInfo: {
    datetime: new Date(),
    createdBy: "",
  },
  identifyingPerson: {
    relationship: "",
    personStatus: "",
    name: "",
    surname: "",
    mobilePhone: "",
    workPhone: "",
    homePhone: "",
  },
  safetyMeasures: {
    serviceIntroduced: false,
    bedRailsUp: false,
    roomIntroduced: false,
    bedsideArrangement: false,
  },
  generalMeasures: {
    wristbandInfo: "",
    oxygenSystem: "",
    windows: "",
    bedBrake: "",
    companionRequirement: "",
  },
  parentInfo: {
    motherFullName: "",
    motherIdentityNumber: "",
    fatherFullName: "",
    fatherIdentityNumber: "",
    motherEducationLevel: "",
    fatherEducationLevel: "",
    motherOccupation: "",
    fatherOccupation: "",
    motherHabits: "",
    motherHabitsDescription: "",
  },
  vitalSigns: {
    heightCm: "",
    weightKg: "",
    temperatureC: "",
    pulsePerMinute: "",
    spo2Percent: "",
    respirationPerMinute: "",
    bloodPressureMmhg: "",
    painLevel: "",
    birthWeightUnderOneYear: "",
    headCircumferenceBaby: "",
  },
  pregnancyAndBirth: {
    referralLocation: "",
    referralLocationDescription: "",
    unitArrivalMethod: "",
    unitArrivalMethodDescription: "",
    pregnancyType: "",
    reduction: "",
    multiplePregnancy: "",
    multiplePregnancyDescription: "",
    numberOfPregnancies: "",
    numberOfBirths: "",
    numberOfMiscarriages: "",
    numberOfLivingChildren: "",
    otherInfo: "",
  },
  needsAndPractices: {
    admissionPractices: "",
    admissionPracticesDescription: "",
    medicationDuringPregnancy: "",
    medicationDuringPregnancyDescription: "",
    problemsDuringPregnancy: "",
    problemsDuringPregnancyDescription: "",
    religiousAndCulturalNeeds: false,
    religiousAndCulturalNeedsDescription: "",
    privacyRequest: false,
    privacyRequestDescription: "",
    specialCoveringPreference: false,
    specialCoveringPreferenceDescription: "",
    specialDressingPreference: false,
    specialDressingPreferenceDescription: "",
  },
  dischargeInfo: {
    method: "",
    methodDescription: "",
    howWillGo: "",
    howWillGoDescription: "",
    destination: "",
    destinationDescription: "",
    homeCareNeeded: false,
    homeCareDescription: "",
    medicalEquipmentNeeded: false,
    medicalEquipmentDescription: "",
    specialRequirements: "",
    motherBabyBond: "",
    primaryCaregiver: "",
    motherWorking: "",
    motherWorkingDescription: "",
    fatherWorking: "",
    fatherWorkingDescription: "",
  },
  medicalHistory: {
    relativesInfo: "",
    relativesInfoDescription: "",
    geneticDiseases: "",
    geneticDiseasesDescription: "",
    hereditaryDiseases: "",
    hereditaryDiseasesDescription: "",
    knownDiseases: "",
    knownDiseasesDescription: "",
    hearingLossHistory: "",
    hearingLossHistoryDescription: "",
    hearingLossDegree: "",
    hearingLossDegreeDescription: "",
    readmittedSiblings: "",
    readmittedSiblingsDescription: "",
    sickSiblingHistory: "",
    sickSiblingHistoryDescription: "",
  },
  neuromuscular: {
    muscleTone: "",
    moroReflex: "",
    suckingReflex: "",
    rootingReflex: "",
    graspReflex: "",
    crying: "",
    generalCondition: "",
  },
  hospitalization: {
    reason: "",
    reasonDescription: "",
    family: "",
    familyDescription: "",
    bloodTransfusion: "",
    bloodTransfusionDescription: "",
    transfusionReaction: "",
  },
  catheters: {
    existingCatheters: "",
    ivCatheterDate: null,
    umbilicalCatheterDate: null,
    other: "",
  },
  dailyLiving: {
    arrivalMethod: "",
    arrivalMethodDescription: "",
    consciousness: "",
    consciousnessDescription: "",
    sleepPattern: "",
    sleepPatternDescription: "",
    excretion: "",
    excretionDescription: "",
    hygieneHabits: "",
    hygieneHabitsDescription: "",
    nutrition: "",
    nutritionDescription: "",
    nutritionType: "",
    nutritionTypeDescription: "",
    appetite: "",
    appetiteDescription: "",
  },
  sensoryAndComm: {
    hearingSpeaking: "",
    hearingSpeakingDescription: "",
    vision: "",
    visionDescription: "",
    prosthetics: "",
    prostheticsDescription: "",
    communicationStatus: "",
    communicationStatusDescription: "",
    psychological: "",
    psychologicalDescription: "",
    schoolStatus: "",
    schoolStatusDescription: "",
    mobility: "",
    mobilityDescription: "",
  },
  systemsAssessment: {
    mouthTongue: "",
    mouthTongueDescription: "",
    respiratory: "",
    respiratoryDescription: "",
    genitourinary: "",
    genitourinaryDescription: "",
    gastrointestinal: "",
    gastrointestinalDescription: "",
    neurological: "",
    neurologicalDescription: "",
    skin: "",
    skinDescription: "",
  },
  appointmentsAndOther: {
    circumcisionRequest: "",
    circumcisionRequestDescription: "",
    birthWeight: "",
    totalBilirubin: "",
    childSeatInfo: "",
    childSeatInfoDescription: "",
    unsafeMedicationsInfo: "",
    unsafeMedicationsInfoDescription: "",
    checkupAppointment: "",
    checkupAppointmentDescription: "",
    hearingTestAppointment: "",
    hearingTestAppointmentDescription: "",
    homeCleaningSupplies: "",
    homeCleaningSuppliesDescription: "",
  },
};

export const useChildPatientAssessmentForm = () => {
  const formMethods = useForm<ChildPatientAssessmentFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues,
  });

  const { handleSubmit } = formMethods;

  const normalizeData = (data: ChildPatientAssessmentFormData): ChildPatientAssessmentFormData => {
    const trimValue = (val: string) => (val ? val.trim() : "");

    return {
      ...data,
      identifyingPerson: {
        ...data.identifyingPerson,
        name: trimValue(data.identifyingPerson.name),
        surname: trimValue(data.identifyingPerson.surname),
      },
      hospitalization: {
        ...data.hospitalization,
        reasonDescription: trimValue(data.hospitalization.reasonDescription),
      },
      dailyLiving: {
        ...data.dailyLiving,
        consciousnessDescription: trimValue(data.dailyLiving.consciousnessDescription),
      }
    };
  };

  const onSubmit = (data: ChildPatientAssessmentFormData) => {
    const normalizedData = normalizeData(data);
    console.log("Submitting Normalized Child Assessment:", normalizedData);
  };

  return {
    formMethods,
    onSubmit: handleSubmit(onSubmit),
  };
};
